import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/solid';
import get from 'lodash/get';
import { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { TaxSummaryContext } from '../../../../state/tax-summary-context';
import { TPaymentPeriodPayment } from '../../../../types/payment-data-types';
import {
  THideDayColumns,
  TTaxSummary,
} from '../../../../types/reporting-history-types';
import { PAYMENT_METHOD_MAP } from '../../constants';
import {
  ExpanderContainer,
  FlexRow,
  ReportingHistoryDataRow,
} from '../../styles';
import { TSupportedLocales, convertToCurrencyFormat } from '../../utils';
import { PaymentDetail } from './payment-detail';
import { PaymentReporter } from './payment-reporter';
import { TaxSummary } from './tax-summary/tax-summary';

const Div = styled.div.attrs({
  className: 'paymentPeriodItemContainer'
})`
  padding: 10px;
  background-color: ${(props: { index: number }) => props.index % 2 ? 'white' : '#f2f2f2'};
`;

const IndicatorContainer = styled.div`
  width: 25px;
  height: 25px;
`;

type TPeriodPaymentProps = THideDayColumns & {
  paymentData: TPaymentPeriodPayment;
  locale: TSupportedLocales;
  breakdownConfig: TTaxSummary;
  index: number;
};

export function PeriodPayment(props: TPeriodPaymentProps) {
  const {
    addPaymentID,
    removePaymentID,
    findPaymentID,
  } = useContext(TaxSummaryContext);

  const taxableReceipts = useMemo(
    () => convertToCurrencyFormat(props.paymentData.taxableReceipts, props.locale),
    [props.paymentData.taxableReceipts],
  );

  const permitPaymentTotal = useMemo(
    () =>
      /**
       * We use "props.paymentData.fractionalPaymentTotal" to only show users how much they
       * paid for that specific permit rather than the entire transaction to avoid confusion
       * with bulk payments.
       */
      convertToCurrencyFormat(props.paymentData.fractionalPaymentTotal, props.locale),
    [props.paymentData.fractionalPaymentTotal],
  );

  const paymentMethod = useCallback(
    (method) => get(PAYMENT_METHOD_MAP, method, method),
    [PAYMENT_METHOD_MAP],
  );

  const displayBreakdownTable = useMemo(() => 
    findPaymentID(props.paymentData.paymentId), 
    [findPaymentID, props.paymentData.paymentId],
  );

  const handleClick = useCallback(() => {
    if (displayBreakdownTable) {
      removePaymentID(props.paymentData.paymentId);
    } else {
      addPaymentID(props.paymentData.paymentId);
    }
  }, [addPaymentID, removePaymentID, props.paymentData.paymentId]);

  return (
    <Div index={props.index}>
      <FlexRow>
        <ReportingHistoryDataRow>
          <PaymentReporter {...props.paymentData} />

          {!props.hideDayColumns && (
            <PaymentDetail size="sm" value={props.paymentData.daysAvailable} />
          )}

          {!props.hideDayColumns && (
            <PaymentDetail size="sm" value={props.paymentData.daysOccupied} />
          )}

          <PaymentDetail size="md" value={taxableReceipts} />

          <PaymentDetail size="md" value={permitPaymentTotal} />

          <PaymentDetail
            size="md"
            value={paymentMethod(props.paymentData.paymentProvider)}
          />
        </ReportingHistoryDataRow>

        <ExpanderContainer onClick={handleClick}>
          <IndicatorContainer>
            {displayBreakdownTable ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </IndicatorContainer>
        </ExpanderContainer>
      </FlexRow>

      {displayBreakdownTable && (
        <TaxSummary
          {...props.breakdownConfig}
          locale={props.locale}
          paymentData={props.paymentData}
        />
      )}
    </Div>
  );
}
